import React from "react";

import type {
  ProcessedSameEventMultiOutcome,
  ProcessedSameEventMultiSelection,
  ProcessedSelection,
} from "@/hooks/firestore/betting/useEntries";

import cx from "classnames";
import { LegWinIcon } from "@picklebet/ui";
import { LegLoseIcon } from "@picklebet/ui";
import { LegPendingIcon } from "@picklebet/ui";
import { LegVoidIcon } from "@picklebet/ui";
import { useBetTracker } from "features/entries/hooks/useBetTracker";
import { deriveInterimResult } from "features/entries/util/bet-tracker-helpers";
import {
  sortSgmOutcomes,
  sortSrmOutcomes,
} from "features/entries/util/selection-helpers";
import {
  isSameEventMultiSelection,
  isSingleSelection,
} from "@/features/entries/util/type-guards";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { Tooltip } from "@picklebet/ui";

import * as styles from "./LegStatus.module.scss";

const LEGS_TO_SHOW = 8;

type LegResult = ProcessedSameEventMultiSelection["result"];

interface LegStatusProps {
  selection: ProcessedSameEventMultiSelection;
  isEntryWon?: boolean;
}

const useOutcomeStat = (
  outcome: ProcessedSameEventMultiOutcome,
  eventId: string,
) => {
  const [winCriteriaId, { criteria, dataType } = {}] =
    Object.entries(outcome.winCriteria ?? {})[0] ?? [];

  const value = useBetTracker(dataType, eventId, winCriteriaId);

  return { value, criteria };
};

const LegStatus = ({ selection, isEntryWon }: LegStatusProps) => {
  const isSRM = selection.event.eventType === "RACE";
  const outcomes = isSRM
    ? sortSrmOutcomes(selection.outcomes)
    : sortSgmOutcomes(selection.outcomes);

  return (
    <div className={styles.legStatusContainer}>
      <div className={cx(styles.legCount, { [styles.isWon]: isEntryWon })}>
        {outcomes.length}
      </div>
      {outcomes.slice(0, LEGS_TO_SHOW).map((outcome) =>
        isSRM ? (
          <div
            key={outcome.outcomeId}
            className={cx(styles.legStatus, { [styles.isWon]: isEntryWon })}
          >
            <StatusIcon finalResult={outcome.outcomeResult} />
          </div>
        ) : (
          <LiveStatusIcon
            outcome={outcome}
            eventId={selection.event.eventId}
            isWon={isEntryWon}
          />
        ),
      )}
      {outcomes.length > LEGS_TO_SHOW && (
        <div
          className={cx(styles.legStatusOverflow, {
            [styles.isWon]: isEntryWon,
          })}
        >
          +{outcomes.length - LEGS_TO_SHOW}
        </div>
      )}
    </div>
  );
};

const MultiLegStatus = ({
  isEntryWon,
  selections,
}: {
  selections: ProcessedSelection[];
  isEntryWon?: boolean;
}) => {
  const getKey = (selection: ProcessedSelection) => {
    if (isSameEventMultiSelection(selection)) {
      return selection.outcomes?.[0]?.outcomeId;
    }
    if (isSingleSelection(selection)) {
      return selection?.outcomeId;
    }
  };

  return (
    <div className={styles.legStatusContainer}>
      <div className={cx(styles.legCount, { [styles.isWon]: isEntryWon })}>
        {selections.length}
      </div>
      {selections.slice(0, LEGS_TO_SHOW).map((selection, i) => (
        <div
          key={getKey(selection) ?? i}
          className={cx(styles.legStatus, { [styles.isWon]: isEntryWon })}
        >
          <StatusIcon finalResult={selection.result} />
        </div>
      ))}
      {selections.length > LEGS_TO_SHOW && (
        <div
          className={cx(styles.legStatusOverflow, {
            [styles.isWon]: isEntryWon,
          })}
        >
          +{selections.length - LEGS_TO_SHOW}
        </div>
      )}
    </div>
  );
};

interface LiveStatusIconProps {
  outcome: ProcessedSameEventMultiOutcome;
  eventId: string;
  isWon?: boolean;
}

const LiveStatusIcon = ({ outcome, eventId, isWon }: LiveStatusIconProps) => {
  const { value, criteria } = useOutcomeStat(outcome, eventId);
  const interimResult = deriveInterimResult(value, criteria);

  return (
    <Tooltip
      contentClassName={styles.tooltipOverrides}
      arrowClassName={styles.tooltipArrow}
      content={
        <div className={styles.tooltipContent}>
          <OutcomeIcon
            outcomeType={outcome.outcomeType}
            competitor={outcome?.competitor ?? undefined}
            className={styles.tooltipIcon}
          />
          <span className={styles.tooltipText}>{outcome.outcomeName}</span>
          {typeof value === "number" && (
            <div className={styles.tooltipStat}>{value}</div>
          )}
        </div>
      }
      trigger={
        <div
          key={outcome.outcomeId}
          className={cx(styles.legStatus, { [styles.isWon]: isWon })}
        >
          <StatusIcon
            finalResult={outcome.outcomeResult}
            interimResult={interimResult}
          />
        </div>
      }
    />
  );
};

interface StatusIconProps {
  interimResult?: LegResult;
  finalResult: LegResult;
}

export const StatusIcon = ({ interimResult, finalResult }: StatusIconProps) => {
  if (finalResult === "WIN") return <LegWinIcon className={styles.wonIcon} />;
  if (finalResult === "LOSE")
    return <LegLoseIcon className={styles.settledIcon} />;
  if (finalResult === "VOID")
    return <LegVoidIcon className={styles.voidIcon} />;
  if (interimResult === "WIN") return <LegWinIcon className={styles.wonIcon} />;
  if (interimResult === "LOSE")
    return <LegLoseIcon className={styles.lostIcon} />;
  if (interimResult === "VOID")
    return <LegVoidIcon className={styles.voidIcon} />;
  return <LegPendingIcon className={styles.pendingIcon} />;
};

export { LegStatus, MultiLegStatus };
